import { Button, Card, Heading, IconButton, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import {
	type IdentifierModel,
	type IssueTypeIdentifier,
	OrganizationAccessLevel,
	type Stored,
} from "@overmap-ai/common"
import { selectIssueTypeById, selectIssueTypeIdentifiersOfIssueType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateIdentifierDialog } from "../../components/Dialogs/CreateIdentifierDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/identifiers")

export const IssueTypeSettingsIdentifiersView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId ?? ""))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const issueTypeIdentifiers = useShallowEqaulSelector(selectIssueTypeIdentifiersOfIssueType(issueType.uuid))

	const handleSubmit = useCallback(
		(name: IdentifierModel["name"]) => {
			void sdk.issueTypeIdentifiers.add({
				issue_type: issueType.uuid,
				name: name,
			})
		},
		[issueType.uuid],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Identifiers</Heading>
					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<CreateIdentifierDialog onSubmit={handleSubmit} size="sm">
							<Button variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Create
							</Button>
						</CreateIdentifierDialog>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<div role="list" className="flex flex-col gap-4">
					{issueTypeIdentifiers.length > 0 ? (
						issueTypeIdentifiers.map((issueTypeIdentifier) => {
							return (
								<IssueTypeIdentifierListItem
									issueTypeIdentifier={issueTypeIdentifier}
									key={issueTypeIdentifier.uuid}
								/>
							)
						})
					) : (
						<Text size="sm" weight="regular" accentColor="base">
							No identifiers.
						</Text>
					)}
				</div>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsIdentifiersView.displayName = "IssueTypeSettingsIdentifiersView"

interface IssueTypeIdentifierListItemProps {
	issueTypeIdentifier: Stored<IssueTypeIdentifier>
}

const IssueTypeIdentifierListItem = memo<IssueTypeIdentifierListItemProps>((props) => {
	const { issueTypeIdentifier } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handleIssueTypeIdentifierNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			void sdk.issueTypeIdentifiers.update({
				uuid: issueTypeIdentifier.uuid,
				name: value,
			})
		},
		[issueTypeIdentifier.uuid],
	)

	const issueTypeIdentifierNameInputProps = useBlurInput<HTMLInputElement>(
		issueTypeIdentifier.name,
		handleIssueTypeIdentifierNameInputBlur,
		true,
	)

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete identifier",
			description: "The identifier will be deleted immediately. This action cannot be undone.",
			onAction: () => sdk.issueTypeIdentifiers.delete(issueTypeIdentifier.uuid),
		})
	}, [issueTypeIdentifier.uuid, openConfirmDeleteDialog])

	return (
		<Card className="flex items-center justify-between gap-2" role="listitem" size="sm">
			<Input.Root variant="outline">
				<Input.Slot>
					<LuIcon icon={modelIconMapping.identifier} />
				</Input.Slot>
				<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...issueTypeIdentifierNameInputProps} />
			</Input.Root>

			<IconButton accentColor="base" size="sm" variant="ghost" disabled={!isAdmin} onClick={handleDelete}>
				<LuIcon icon={modelIconMapping.delete} />
			</IconButton>
		</Card>
	)
})
IssueTypeIdentifierListItem.displayName = "IssueTypeIdentifierListItem"
